import { GameConstants, InputActions, type Layer, ObjectCategory } from "@common/constants";
import { JumpIn } from "@common/definitions/jumpIn";
import { Tornadoes } from "@common/definitions/storm";
import { type JumpInData } from "@common/packets/updatePacket";
import { fallLeft, mayJump, planeAt, planeClock, routeHeading, routeMs, steerDirection, type JumpRoute } from "@common/utils/jumpIn";
import { EaseFunctions, Numeric } from "@common/utils/math";
import { FloorNames, FloorTypes } from "@common/utils/terrain";
import { Vec, type Vector } from "@common/utils/vector";
import { Container, Graphics, Sprite, Text, Texture } from "pixi.js";
import { GameConsole } from "../console/gameConsole";
import { Game } from "../game";
import { type Obstacle } from "../objects/obstacle";
import { type Player } from "../objects/player";
import { PIXI_SCALE } from "../utils/constants";
import { TrenchioSprite, toPixiCoords } from "../utils/pixi";
import { t } from "../utils/translations/phrases";
import { RIGHT_TO_LEFT, canvasLine, spacedLetters } from "../utils/translations/languageLook";
import { selectedLanguageCode } from "../utils/translations/translations";
import { WarLook } from "../war/warLook";
import { Lighting } from "./lighting";
import { WarSky } from "../war/warSky";
import { CameraManager } from "./cameraManager";
import { InputManager } from "./inputManager";
import { deckSolid, Drift, LandingRing, landingSolid, mapRoofs, mapRoofTops, type RoofTops, type RoofView } from "./jumpInLogic";
import { MapManager } from "./mapManager";
import { ParticleManager } from "./particleManager";
import { SoundManager, type GameSound } from "./soundManager";

/**
 * Jumping in (1.06, common/src/definitions/jumpIn.ts) as the page shows it, after the idea's picture the owner liked
 * ("the ivsual you made is already good"): the two planes over the map (the airdrop's plane, its shadow on the ground,
 * their ways and where they are on the minimap), everyone under a chute (the airdrop's chute in a colour of their own,
 * yours blue, marked YOU; the higher, the bigger and the farther from its shadow), the ring where you'll come down,
 * the dust of a landing and the chute folding, and the HUD: JUMP IN with the key to press, how many are still aboard,
 * your height. The server decides it all: the planes' clock and ways come with the updates (`JumpInData`), every
 * player's height with theirs (`air`).
 */

/** players in the air and their chutes, in the sky's container (over the world's, the gas's cloud and the fog: `_sky`): over the ring and the shadows there, under the planes */
export const AIR_ZINDEX = 950;
/** the shadows of the planes and the chutes, and the ring where you'll land: on the ground, over every roof */
export const AIR_GROUND_ZINDEX = 5;
/** the planes: over everything in the world, the chutes too (the airdrop's plane, a shadow, is drawn at MAX_SAFE_INTEGER - 2) */
const PLANE_ZINDEX = Number.MAX_SAFE_INTEGER - 3;

/** your chute: the game's blue (the airdrop chute's own) */
export const OWN_CHUTE = 0x5a70e0;
/** everyone else's: each a colour of its own (by their id), none of them blue */
export const CHUTE_COLORS = [0xd9483b, 0x3f9d4e, 0xe08a2c, 0x8e5bd6, 0xe3c33a, 0x2fa89c, 0xd65b9b, 0xa3a3a3, 0x86a83a, 0xb06a32] as const;
export const chuteColor = (id: number, own: boolean): number => own ? OWN_CHUTE : CHUTE_COLORS[Math.abs(id) % CHUTE_COLORS.length];

/** how much bigger someone is drawn `height` m up (nearer the eye): half again at the top */
export const airScale = (height: number): number => 1 + height / 160;
/** how far up and to the left of their shadow someone `height` m up is drawn (units) */
export const airOffset = (height: number): Vector => Vec(-0.03 * height, -0.075 * height);
/** the canopy: this far above its player's middle, and this wide, on the ground (units; times `airScale` in the air) */
const CANOPY_UP = 7;
const CANOPY_WIDTH = 10;
/** the airdrop chute's picture's width (px) */
const CANOPY_PICTURE = 700;
/**
 * the planes' size: their picture (airdrop/shared/jump_plane.svg, tools/jumpin/jump_plane.py) is the airdrop plane's, 26 by
 * 38 units at 1; at 3.2 a plane is 84 units long and 120 across its wings, more than half the screen's height when
 * you're aboard (the camera's at 145 units then)
 */
export const PLANE_SCALE = 3.2;
/** how far a plane's shadow falls from it on the ground (units) */
const PLANE_SHADOW = 26;
/** your own plane is see-through (the ground under it is what you're choosing), the other one as it is */
export const OWN_PLANE_ALPHA = 0.55;
/** the planes' shadows on the ground: their own picture, black */
const PLANE_SHADOW_ALPHA = 0.2;
/** the ring and the words on the ground, drawn this big on the screen whatever the camera's zoom (pixels) */
const RING_LINE = 3;
const RING_DOT = 4;
const LABEL_PX = 18;
/** the words' pictures are drawn at this size and scaled to LABEL_PX on the screen (crisp: never scaled up much) */
const LABEL_FONT = 64;
/**
 * the HUD round the screen's edges, which the ring's arrow and words keep clear of (`_ringFrame`): the minimap and the
 * gas's clock, the top middle's column (the banner, the gas's words), the top right, the height bar, the ammo, the
 * bottom row, the chips; a phone's buttons
 */
const FRAME_AVOIDS = [
    "top-left-container", "mobile-options", "center-top-container", "jump-hud", "ui-kill-leader", "counter-holder", "kill-feed",
    "th-controls-chip", "th-flashlight-chip", "btn-flashlight", "jump-height", "ammos-container", "center-bottom-container",
    "healing-items-container", "trench-weapon", "weapons-container", "equipment-container", "blood-hud", "bag-meter", "trench-hints"
] as const;
/**
 * on a short screen (a phone's), the ground point is this much of the screen under the middle as you come down (the
 * server's view reaches up as far: `JumpIn.shortLift`)
 */
const SHORT_LIFT = JumpIn.shortLift;
/**
 * aimed somewhere new over a roof or the water, the ring's open ground is looked for again at most this often (the
 * server's chute looks every quarter of a second: `JumpInManager.settleSpot`)
 */
const SETTLE_SEARCH_MS = 120;
/** the own chute's drift, for the ring, over this long of its updates (ms: `noteOwn`) */
const DRIFT_WINDOW_MS = 200;
/** a jump pressed this soon after coming aboard isn't kept for the end of the moment's wait (a press from the menu) */
const JUMP_KEPT_AFTER = 150;


/** a soft round shadow, made once */
let softTexture: Texture | undefined;
const soft = (): Texture => {
    if (softTexture) return softTexture;
    const canvas = document.createElement("canvas");
    canvas.width = canvas.height = 128;
    const ctx = canvas.getContext("2d");
    if (ctx) {
        const gradient = ctx.createRadialGradient(64, 64, 0, 64, 64, 64);
        gradient.addColorStop(0, "rgba(0, 0, 0, 0.9)");
        gradient.addColorStop(0.55, "rgba(0, 0, 0, 0.5)");
        gradient.addColorStop(1, "rgba(0, 0, 0, 0)");
        ctx.fillStyle = gradient;
        ctx.fillRect(0, 0, 128, 128);
    }
    softTexture = Texture.from(canvas);
    return softTexture;
};

interface ChuteView {
    /** the canopy and its cords, over the player */
    readonly container: Container
    readonly canopy: TrenchioSprite
    readonly cords: Graphics
    /** on the ground: the chute's shadow (wide, faint) and the body's (it meets the body as it lands) */
    readonly shadow: Sprite
    readonly bodyShadow: Sprite
    /** YOU, over your own */
    readonly you?: Container
    /** where its player is on the ground (pixels) and how high, as last drawn */
    ground: Vector
    height: number
    /** when the chute opened (performance.now()): it blooms over `OPEN_MS` (1.09) */
    readonly opened: number
    /** the last frame's time, and the drift (units a ms) read off the ground point from frame to frame, smoothed */
    lastAt?: number
    drift: Vector
    /** how far the canopy is flared by the brake (0 to 1, eased: 1.09), and whether the brake was on last frame */
    flare: number
    braked: boolean
    /** when the flare was last heard (performance.now()) */
    flaredAt: number
}

/** the canopy blooms open over this long after the jump (1.09), with a little overshoot, the cords drawing taut */
const OPEN_MS = 650;
/** the brake's flare comes on and off over about this long */
const FLARE_MS = 220;
/** a canopy leads its body across the ground by this much of a second's drift (banking into the glide) */
const LEAD_MS = 55;

interface PlaneView {
    readonly sprite: TrenchioSprite
    readonly shadow: TrenchioSprite
    sound?: GameSound
}

/** what the HUD is showing (its words are set only when this changes) */
type HudState = "hidden" | "boarding" | "aboard" | "falling" | "tossed";

class JumpInViewClass {
    private _data?: JumpInData;
    /** performance.now() when the planes' clock read 0 (from each update's `elapsed`, smoothed) */
    private _zero?: number;
    /** when the player on screen came aboard (performance.now()): `JumpIn.boardHoldMs` before they can jump */
    private _boardedAt?: number;
    private _planes?: [PlaneView, PlaneView];
    private readonly _chutes = new Map<Player, ChuteView>();
    /** where the player on screen is on the ground, and how they drift (units a ms, from their last updates) */
    private _ownGround?: Vector;
    private _ownLast?: Vector;
    private _ownVelocity = Vec(0, 0);
    /** the own chute's drift from its updates of the last moments, as they came (`noteOwn`) */
    private readonly _drift = new Drift(DRIFT_WINDOW_MS, 1000 / GameConstants.tps);

    private _ring?: Graphics;
    private _ringLabel?: Text;
    private _ringWords = "";
    /** the ring's arrow at the screen's edge, when the ring's off it (steering hard, it's hundreds of units away) */
    private _ringArrow?: Graphics;
    /** chutes folding on the ground after a landing (a reset takes them away at once) */
    private readonly _folding = new Set<Container>();
    /** movement smoothed this frame (cv_movement_smoothing) */
    private _smoothing = true;
    /** the part of the screen the ring's arrow keeps to, as last read from the HUD (`_ringFrame`) */
    private _frame?: { readonly left: number, readonly top: number, readonly right: number, readonly bottom: number, readonly width: number, readonly height: number, readonly until: number };
    /**
     * on a short screen, how far the camera is lifted now (screen pixels): eased up as you jump and down as you land,
     * not a jolt (1.06's audit)
     */
    private _lift = 0;
    private _liftAt?: number;
    /** the minimap's ways cleared, the planes gone: nothing to draw again */
    private _minimapClear = false;
    /** the map's roofs and walls (every building of the map's, off the screen too: `_mapRoofs`), made once a map */
    private _roofs?: { readonly from: readonly unknown[], readonly list: readonly RoofView[] };

    /** the map's roofs' decks and glass (1.08: `_roofTops`), made once a map */
    private _tops?: { readonly from: readonly unknown[], readonly tops: Pick<RoofTops, "decks" | "glass"> };
    /**
     * where the ring goes (jumpInLogic.ts `LandingRing`): aimed at a roof or the water, the open ground the server takes
     * the chute to in its last seconds (`JumpInManager.settleSpot`), looked for at most every `SETTLE_SEARCH_MS`
     */
    private readonly _landing = new LandingRing(SETTLE_SEARCH_MS);
    /**
     * the server's word on it, in your chute's final approach (PlayerData `landing`): the open ground its chute takes
     * yours to, where the ring is then (1.06.1's second review: this page's own reckoning of it could differ, by a few
     * units along a wall or, from where two walls are as near, the other side of a roof)
     */
    private _serverSpot?: Vector;

    /** the server's word on where the final approach takes your chute (none: it comes down where it's steered) */
    landingSpot(spot: Vector | undefined): void {
        this._serverSpot = spot ? Vec.clone(spot) : undefined;
    }

    /**
     * the sky over the world (1.06's lab): the planes, everyone under a chute and the ring, over the gas's cloud and the
     * fog, which WarLook draws over the world's container (its fx layer); with the camera's place and scale, each frame
     * (`afterCamera`). Beyond the first zone the gas's cloud covers the ground from the start, where the planes come in
     */
    private _sky?: Container;

    private _hud?: {
        readonly root: HTMLElement
        readonly title: HTMLElement
        readonly line1: HTMLElement
        readonly line2: HTMLElement
        readonly count: HTMLElement
        readonly height: HTMLElement
        readonly heightLabel: HTMLElement
        readonly heightFill: HTMLElement
        readonly heightMarker: HTMLElement
        readonly heightValue: HTMLElement
        readonly button: HTMLButtonElement
    };

    private _hudKey = "";

    /** the planes' data, as an update brings it */
    updateFrom(data: JumpInData): void {
        const now = performance.now();
        const zero = now - data.elapsed;
        // (a clock that runs on its own between updates: the next update's word only nudges it, a big gap resets it)
        this._zero = this._zero === undefined || Math.abs(zero - this._zero) > 250 ? zero : this._zero + (zero - this._zero) * 0.2;
        const before = this._data;
        this._data = data;
        if (data.plane !== undefined && before?.plane === undefined) this._boardedAt = now;
        if (data.plane === undefined) this._boardedAt = undefined;
        if (data.over) this._dropPlanes();
    }

    /** a view's catch-up is being read (`noteUpdate`, till the message's end: `messageRead`) */
    private _catchingUp = false;

    /**
     * a view's catch-up comes (the death replay's start, a watch's, a switch of whose view you watch): seconds of updates
     * read at once, the planes' clock set from the last of them with the planes' data, up to a resync (2 s) older than
     * the view (`noteUpdate`; 1.06.1's audit: the planes were drawn behind, then jumped on)
     */
    catchUp(): void {
        this._catchingUp = true;
    }

    /**
     * an update read (`hasData`: with the planes' data): in a catch-up, each one read after the planes' data, in the same
     * moment, puts their clock a tick further on; the message's end ends the catch-up (`messageRead`)
     */
    noteUpdate(hasData: boolean): void {
        if (!this._catchingUp) return;
        if (!hasData && this._zero !== undefined) this._zero -= 1000 / GameConstants.tps;
    }

    /**
     * a message read to its end: a catch-up's updates come in one (the server's batch), so it's over (1.06.1's second
     * review: it ended at the first pause of 4 ms between two updates, and one heavy update on a slow phone ended it
     * early, the planes drawn behind)
     */
    messageRead(): void {
        this._catchingUp = false;
    }

    /** ms since the first player joined, now */
    elapsed(): number | undefined {
        return this._zero === undefined ? undefined : performance.now() - this._zero;
    }

    private _planeTime(plane: number): number | undefined {
        const elapsed = this.elapsed();
        return elapsed === undefined ? undefined : planeClock(plane, elapsed);
    }

    /** where a plane is drawn now (its clock past its way's end: flying on) */
    planePosition(plane: number): Vector | undefined {
        const route = this._data?.routes[plane];
        const time = this._planeTime(plane);
        if (!route || time === undefined) return undefined;
        return planeAt(route, time);
    }

    /** the active player is aboard, and the plane is over its way: Space would jump */
    private _canJump(): boolean {
        const data = this._data;
        const plane = data?.plane;
        if (!data || plane === undefined || this._boardedAt === undefined) return false;
        const time = this._planeTime(plane);
        return time !== undefined && mayJump(time, performance.now() - this._boardedAt) && time < routeMs(data.routes[plane]);
    }

    /**
     * the screen is yours, playing: not a spectator's, a lobby watcher's or the replay of someone's last moments (a
     * replay is the killer's own packets: their chute would be the blue one, marked YOU: 1.06's audit)
     */
    private _playing(): boolean {
        return !Game.spectating && !Game.delayedView && !Game.replaying;
    }

    /** `player` is the one playing on this screen */
    private _mine(player: Player): boolean {
        return player.isActivePlayer && this._playing();
    }

    /**
     * the jump command (Space, a phone's button): sent only while it would jump; pressed in the moment after coming
     * aboard (`JumpIn.boardHoldMs`, the plane over its way), kept and sent as that's over (a late boarder's press, the
     * HUD saying to press it, was lost: 1.06.1's audit)
     */
    jump(): void {
        if (!this._playing()) return;
        if (this._canJump()) {
            this._jumpKept = undefined;
            InputManager.addAction(InputActions.Jump);
            return;
        }
        const plane = this._data?.plane;
        const time = plane !== undefined ? this._planeTime(plane) : undefined;
        if (this._boardedAt !== undefined && time !== undefined && time >= 0 && performance.now() - this._boardedAt >= JUMP_KEPT_AFTER) {
            this._jumpKept = performance.now();
        }
    }

    /** a press in the moment after coming aboard, kept (performance.now()) */
    private _jumpKept?: number;

    /** a kept press goes out once the plane may be jumped from (forgotten after a moment, or out of the plane) */
    private _sendKeptJump(): void {
        const kept = this._jumpKept;
        if (kept === undefined) return;
        if (!this._playing() || this._data?.plane === undefined || performance.now() - kept > JumpIn.boardHoldMs + 500) {
            this._jumpKept = undefined;
            return;
        }
        if (!this._canJump()) return;
        this._jumpKept = undefined;
        InputManager.addAction(InputActions.Jump);
    }

    /**
     * the player on screen moved (an update, in the air): their drift, for the ring, over the last fifth of a second or
     * so of updates as they came (1.06.1's second review: worked out from each update and the time since the one
     * before, on a busy connection, updates bunched and spread, it was off by half and more, and the ring's aim with it)
     */
    noteOwn(position: Vector): void {
        this._drift.note(performance.now(), position);
        this._ownVelocity = this._drift.velocity;
        this._ownLast = Vec.clone(position);
    }

    /**
     * A player in the air, each frame (`update`, whether movement is smoothed or not: 1.06's audit): aboard, not drawn
     * (they're in the plane); under a chute, drawn up and to the left of where they are on the ground, bigger the higher,
     * their canopy over them, their shadows under them. Where they are on the ground is where the game draws them
     * (`drawnPosition`), never read back from their container, which is moved up here every frame.
     */
    placeAirborne(player: Player): void {
        const air = player.air;
        if (!air) return;
        const container = player.container;
        if (air.aboard) {
            container.visible = false;
            if (player.isActivePlayer) this._ownGround = undefined;
            return;
        }
        container.visible = !player.dead;
        // (no aim trail up there, movement smoothed or not)
        if (player.images.aimTrail) player.images.aimTrail.visible = false;
        if (player.images.altAimTrail) player.images.altAimTrail.visible = false;
        // spun in a tornado (0.98.4): no chute, lifted and turning, their shadow left on the ground under them
        if (air.tossed) {
            this._placeTossed(player, air.height);
            return;
        }
        const view = this._chutes.get(player) ?? this._makeChute(player);
        const height = air.height;
        const scale = airScale(height);
        const now = performance.now();
        // (smoothed, where it's drawn between its updates; not, where it is)
        const ground = toPixiCoords(this._smoothing ? player.drawnPosition : player.position);
        // the drift across the ground, read off the frames (the own chute's the ring's reckoning, `noteOwn`): the canopy
        // banks into it and leads the body (1.09)
        const dt = view.lastAt === undefined ? 0 : Math.min(100, now - view.lastAt);
        if (dt > 0) {
            const step = Vec(((ground.x - view.ground.x) / PIXI_SCALE) / dt, ((ground.y - view.ground.y) / PIXI_SCALE) / dt);
            // (a jump in place, a resync's or a teleport's, isn't drift)
            if (Vec.len(step) < JumpIn.settleSpeed * 2) view.drift = Vec.lerp(view.drift, step, 1 - Math.exp(-dt / 160));
        }
        view.lastAt = now;
        view.ground = ground;
        view.height = height;
        if (player.isActivePlayer) this._ownGround = ground;
        const drift = player.isActivePlayer ? this._ownVelocity : view.drift;
        const lean = Vec(Numeric.clamp(drift.x / JumpIn.glideSpeed, -1, 1), Numeric.clamp(drift.y / JumpIn.glideSpeed, -1, 1));

        // the chute opening (1.09): the canopy blooms from a bundle with a little overshoot, the cords drawing taut,
        // the body jerked as it fills; the brake's flare (the jump key held): the canopy wider and higher, the body
        // swung back under it
        const open = Numeric.clamp((now - view.opened) / OPEN_MS, 0, 1);
        const bloom = open >= 1 ? 1 : 1 + 2.70158 * (open - 1) ** 3 + 1.70158 * (open - 1) ** 2;
        // (your own, as the key is held, not as the server echoes it back: the canopy flares with the HUD)
        const braked = player.isActivePlayer && this._playing() ? InputManager.jumpHeld : air.braking === true;
        // (the flare's sound once a flare, not for every tap of the key: 1.10.1's audit)
        if (braked && !view.braked && now - view.flaredAt > 400) {
            view.flaredAt = now;
            player.playSound("chute_flare", { falloff: 0.5, maxRange: 100 });
        }
        view.braked = braked;
        const flareTo = braked ? 1 : 0;
        view.flare += (flareTo - view.flare) * (dt > 0 ? 1 - Math.exp(-dt / FLARE_MS) : 1);
        const flare = view.flare;

        const sway = Math.sin(now / 650 + player.id);
        const bob = Math.sin(now / 430 + player.id * 0.7);
        const offset = airOffset(height);
        const body = Vec(ground.x + offset.x * PIXI_SCALE, ground.y + offset.y * PIXI_SCALE);
        container.position.set(body.x, body.y);
        container.scale.set(player.sizeMod * scale);
        // (hanging under it, facing up: the hands on the cords; swung as the canopy fills, and back as it's braked)
        const jerk = open < 1 ? 0.35 * Math.sin(open * Math.PI * 2) * (1 - open) : 0;
        container.rotation = -Math.PI / 2 + 0.05 * sway + jerk - 0.12 * flare + 0.08 * lean.x;
        container.alpha = 1;
        // (the highest drawn last: nearest the eye)
        container.zIndex = AIR_ZINDEX + height / 100;
        const sky = this._skyLayer();
        if (container.parent !== sky) sky.addChild(container);
        // at night, as moonlight would have them (they're over the night's dark); in the sky's light (dusk, a storm's)
        const tint = WarSky.airTint(0xa6b0cc);
        container.tint = tint;

        // the canopy: over the body, leading it into the glide, higher and wider when braked, bobbing a little
        const leadPx = LEAD_MS * PIXI_SCALE;
        const canopyUp = CANOPY_UP * (1 + 0.18 * flare) * bloom;
        const canopy = Vec(
            body.x + 0.4 * sway * PIXI_SCALE + drift.x * leadPx * scale,
            body.y - canopyUp * scale * PIXI_SCALE + drift.y * leadPx * 0.6 * scale + 0.25 * bob * scale * PIXI_SCALE
        );
        view.container.position.set(canopy.x, canopy.y);
        view.container.zIndex = AIR_ZINDEX + height / 100 + 0.005;
        view.container.tint = tint;
        const base = (CANOPY_WIDTH * PIXI_SCALE / CANOPY_PICTURE) * scale * (0.12 + 0.88 * bloom);
        // (banking: turned into a sideways glide, foreshortened a touch into one up or down the screen)
        view.canopy.scale.set(base * (1 + 0.14 * flare) * (1 - 0.05 * Math.abs(lean.x)), base * (1 - 0.06 * flare) * (1 - 0.07 * Math.abs(lean.y)));
        view.canopy.rotation = 0.05 * sway + 0.22 * lean.x;
        view.canopy.alpha = 0.55 + 0.45 * open;

        // the cords: from each hand to the canopy's rim, two each (drawn taut as the canopy opens)
        const radius = (CANOPY_WIDTH / 2) * PIXI_SCALE * scale * 0.92 * (0.12 + 0.88 * bloom) * (1 + 0.14 * flare);
        const cords = view.cords.clear();
        const hand = (sprite: TrenchioSprite): Vector => {
            // a hand's place in the container, turned and scaled with it, to the canopy's own space
            const c = Math.cos(container.rotation), s = Math.sin(container.rotation);
            const k = container.scale.x;
            return Vec(body.x + (sprite.x * c - sprite.y * s) * k - canopy.x, body.y + (sprite.x * s + sprite.y * c) * k - canopy.y);
        };
        const left = hand(player.images.leftFist), right = hand(player.images.rightFist);
        for (const [from, angles] of [[left, [2.05, 2.55]], [right, [0.6, 1.1]]] as const) {
            for (const angle of angles) {
                const a = angle + 0.05 * sway + 0.22 * lean.x;
                cords.moveTo(from.x, from.y).lineTo(Math.cos(a) * radius, Math.sin(a) * radius);
            }
        }
        cords.stroke({ width: 1.6 * scale, color: 0x1c1a16, alpha: 0.75 * (0.3 + 0.7 * open) });

        // the shadows: the chute's wide and faint, the body's smaller and darker, nearer the higher it is (the canopy's
        // where the canopy is, ahead of the body as it leads)
        const up = height / JumpIn.height;
        view.shadow.position.set(ground.x + 1.2 * PIXI_SCALE + drift.x * leadPx, ground.y - 3 * PIXI_SCALE * (1 - up * 0.5) + drift.y * leadPx * 0.6);
        view.shadow.width = view.shadow.height = (CANOPY_WIDTH * 1.3 + height * 0.06) * PIXI_SCALE * (0.2 + 0.8 * bloom) * (1 + 0.1 * flare);
        view.shadow.alpha = 0.22 * (1 - up * 0.6) * open;
        view.bodyShadow.position.set(ground.x, ground.y);
        view.bodyShadow.width = view.bodyShadow.height = (5.2 + height * 0.03) * PIXI_SCALE;
        view.bodyShadow.alpha = 0.5 * (1 - up * 0.65);

        if (view.you) {
            // (as big on the screen at every zoom: the camera's 145 units in the air shrink a world-sized word to nothing)
            const k = this._pxPerScreenPx();
            view.you.scale.set((LABEL_PX * 1.15 * k) / LABEL_FONT);
            view.you.position.set(0, -(CANOPY_WIDTH / 2) * PIXI_SCALE * scale - 10 * k);
        }
    }

    /** the players spun in a tornado (0.98.4): their shadows, and how far round they've turned */
    private readonly _tossed = new Map<Player, { readonly shadow: Sprite, turn: number, lastAt: number }>();

    /**
     * a player spun in a tornado (0.98.4, `air.tossed`): drawn in the sky's container, lifted up the screen and bigger
     * the higher (`height`, m: up to 9), turning round and round, arms out, their shadow on the ground where they are
     */
    private _placeTossed(player: Player, height: number): void {
        const container = player.container;
        if (container.parent !== this._skyLayer()) this._skyLayer().addChild(container);
        let view = this._tossed.get(player);
        const now = performance.now();
        if (!view) {
            const shadow = new Sprite(soft());
            shadow.anchor.set(0.5);
            shadow.zIndex = AIR_GROUND_ZINDEX;
            CameraManager.addObject(shadow);
            view = { shadow, turn: 0, lastAt: now };
            this._tossed.set(player, view);
            player.playSound("tornado_lift", { falloff: 0.4, maxRange: 120 });
        }
        const dt = Math.min(100, now - view.lastAt);
        view.lastAt = now;
        // (four turns in the five seconds, as the server turns them; the body a touch faster, flailing)
        view.turn += dt * 0.0062;
        const up = height / 9;
        const ground = toPixiCoords(this._smoothing ? player.drawnPosition : player.position);
        const scale = player.sizeMod * (1 + 0.5 * up);
        container.position.set(ground.x + 1.5 * up * PIXI_SCALE, ground.y - 5 * up * PIXI_SCALE);
        container.scale.set(scale);
        container.rotation = view.turn;
        container.zIndex = AIR_GROUND_ZINDEX + 2;
        view.shadow.position.set(ground.x, ground.y);
        view.shadow.width = view.shadow.height = (4.5 + 2 * up) * PIXI_SCALE;
        view.shadow.alpha = 0.42 * (1 - 0.5 * up);
        view.shadow.visible = true;
    }

    /** a player no longer spun (set down, or gone): their shadow gone (`landed` and `putOut` draw them on foot again) */
    private _untoss(player: Player): void {
        const view = this._tossed.get(player);
        if (!view) return;
        view.shadow.destroy();
        this._tossed.delete(player);
    }

    /** the sky's container, over the world's (and the gas's cloud over it): made once, at the end of the world's group */
    private _skyLayer(): Container {
        const world = CameraManager.container.parent;
        let sky = this._sky;
        if (!sky || sky.destroyed) {
            sky = this._sky = new Container({ label: "jump-in sky" });
            sky.sortableChildren = true;
        }
        if (world && sky.parent !== world) world.addChildAt(sky, world.getChildIndex(CameraManager.container) + 1);
        return sky;
    }

    /** every frame, after the camera has moved: the ring placed by it, and the sky moves and zooms with it */
    afterCamera(): void {
        // (by the camera as it's drawn this frame: placed before it moved, the ring's arrow was a frame's travel off the
        // screen's edge, near the middle at a few frames a second: 1.06's lab)
        this._updateRing(Game.activePlayer);
        const sky = this._sky;
        if (!sky || sky.destroyed || sky.children.length === 0) return;
        const camera = CameraManager.container;
        // (kept right over the fog's and the gas cloud's layer, or the world's container: the rain and the snow still
        // fall over the planes; WarLook makes its layers again at each match's start)
        const world = camera.parent;
        if (world) {
            const fx = WarLook.fxLayer;
            const below = fx?.parent === world ? fx : camera;
            const under = world.getChildIndex(below);
            if (sky.parent !== world) world.addChildAt(sky, under + 1);
            else {
                const at = world.getChildIndex(sky);
                if (at !== under + 1) world.setChildIndex(sky, at < under ? under : under + 1);
            }
        }
        sky.position.copyFrom(camera.position);
        sky.scale.copyFrom(camera.scale);
    }

    /** how many world pixels one pixel of the screen is now (the camera's scale turned round) */
    private _pxPerScreenPx(): number {
        const scale = CameraManager.container.scale.x;
        return scale > 0.001 ? 1 / scale : 1;
    }

    private _makeChute(player: Player): ChuteView {
        const container = new Container();
        const canopy = new TrenchioSprite("jump_chute");
        canopy.tint = chuteColor(player.id, this._mine(player));
        const cords = new Graphics();
        container.addChild(cords, canopy);
        let you: Container | undefined;
        if (this._mine(player)) {
            you = new Container();
            const language = selectedLanguageCode();
            const label = new Text({
                text: canvasLine(t("YOU"), language),
                style: {
                    fontFamily: "TH-Anton, Anton, Impact, sans-serif",
                    fontSize: LABEL_FONT,
                    fill: 0xffffff,
                    letterSpacing: spacedLetters(language) ? 3 : 0,
                    dropShadow: { color: 0x000000, alpha: 0.6, blur: 6, distance: 2 }
                }
            });
            label.anchor.set(0.5, 1);
            label.position.set(0, -24);
            const arrow = new Graphics().poly([-22, -16, 22, -16, 0, 10]).fill({ color: 0xffffff }).stroke({ width: 3, color: 0x000000, alpha: 0.35 });
            you.addChild(label, arrow);
            container.addChild(you);
        }
        const shadow = new Sprite(soft());
        shadow.anchor.set(0.5);
        shadow.zIndex = AIR_GROUND_ZINDEX;
        const bodyShadow = new Sprite(soft());
        bodyShadow.anchor.set(0.5);
        bodyShadow.zIndex = AIR_GROUND_ZINDEX + 0.01;
        // (the canopy in the sky, its shadows on the ground)
        this._skyLayer().addChild(container);
        CameraManager.addObject(shadow, bodyShadow);
        // when it opened, read off its height (a chute drawn for the first time well down its fall, come into view, a
        // spectate's switch, drawn again, or a page that lost frames at the jump: drawn as far open as it is by now,
        // flared if braked); one just opened (at the top: a jump, a late joiner's) blooms, with the whump (1.09,
        // tools/sfx/jumpin_sounds.py)
        const air = player.air;
        const braked = air?.braking === true;
        // (a braked chute comes down slower: by its height it's been open longer than it looks; 1.10.1's audit)
        const sinceOpen = air === undefined ? 0 : (1 - Numeric.clamp(air.height / JumpIn.height, 0, 1)) * JumpIn.fallMs / (braked ? JumpIn.brakeRate : 1);
        const fresh = sinceOpen < OPEN_MS;
        const now = performance.now();
        const view: ChuteView = {
            container, canopy, cords, shadow, bodyShadow, you, ground: toPixiCoords(player.position), height: air?.height ?? JumpIn.height,
            opened: now - sinceOpen, drift: Vec(0, 0), flare: braked ? 1 : 0, braked, flaredAt: -Infinity
        };
        this._chutes.set(player, view);
        if (fresh) player.playSound("chute_open", { falloff: 0.5, maxRange: 120 });
        return view;
    }

    /** a player out of their plane: drawn as they are again (not yet under the chute: the next frame places them) */
    jumped(player: Player): void {
        player.container.visible = !player.dead;
    }

    /**
     * a player down on the ground (on `layer`: a deck's, the ground): a puff of dust, their chute folding beside them,
     * and drawn as on foot again
     */
    landed(player: Player, layer: Layer = player.layer): void {
        const view = this._chutes.get(player);
        this._chutes.delete(player);
        this._untoss(player);
        this._ground(player);
        // (the landing's knees: drawn a little big as they touch down, settling to size: 1.09)
        const size = player.sizeMod;
        player.container.scale.set(size * 1.22);
        player.anims.land?.kill();
        player.anims.land = Game.addTween({
            target: player.container.scale, to: { x: size, y: size }, duration: 320, ease: EaseFunctions.backOut,
            onComplete: () => { player.anims.land = undefined; }
        });
        if (player.isActivePlayer) {
            this._ownGround = undefined;
            this._ownLast = undefined;
            this._ownVelocity = Vec(0, 0);
            this._drift.reset();
            this._landing.reset();
            this._serverSpot = undefined;
        }
        const at = player.position;
        // the dust, in a ring round them
        for (let i = 0; i < 7; i++) {
            const angle = (i / 7) * Math.PI * 2 + 0.4;
            ParticleManager.spawnParticle({
                frames: "smoke_particle",
                zIndex: AIR_GROUND_ZINDEX + 0.2,
                position: Vec.add(at, Vec.fromPolar(angle, 1.5)),
                layer,
                lifetime: 900,
                // (units a second: the ring spreads; 1.10.1's audit: written as a millisecond's, it stood still)
                speed: Vec.fromPolar(angle, 3.2),
                scale: { start: 0.35, end: 0.9 },
                alpha: { start: 0.5, end: 0 },
                tint: 0xb09870
            });
        }
        // (the boots on what they land on: a deck's stone or metal, a street, the grass: 1.10.1's audit)
        const floor = MapManager.terrain.getFloor(player.position, layer);
        const stepFloor = FloorTypes[floor]?.slippery ? FloorNames.Ice : floor;
        player.playSound(`${stepFloor}_step_1`, { falloff: 0.6, maxRange: 48, layer });
        if (!view) return;
        // the chute settles beside them, flattening as it folds, and fades
        view.cords.destroy();
        // (the YOU word's picture and its arrow too: 1.06's audit)
        view.you?.destroy({ children: true });
        view.shadow.destroy();
        view.bodyShadow.destroy();
        const { container, canopy } = view;
        this._folding.add(container);
        // (on the ground now, under whatever's over the ground)
        CameraManager.addObject(container);
        container.zIndex = AIR_GROUND_ZINDEX + 0.1;
        const from = Vec(container.position.x, container.position.y);
        const to = toPixiCoords(Vec.add(at, Vec(2.5, -2.2)));
        const start = performance.now();
        const fold = (): void => {
            if (canopy.destroyed) return;
            const u = Numeric.clamp((performance.now() - start) / 600, 0, 1);
            const ease = u * u * (3 - 2 * u);
            container.position.set(Numeric.lerp(from.x, to.x, ease), Numeric.lerp(from.y, to.y, ease));
            const base = (CANOPY_WIDTH * PIXI_SCALE) / CANOPY_PICTURE;
            canopy.scale.set(base * (1 + 0.25 * ease), base * (1 - 0.62 * ease));
            canopy.rotation = 0.3 * ease;
            const age = performance.now() - start;
            container.alpha = 1 - Numeric.clamp((age - 900) / 700, 0, 1);
            if (age >= 1600) {
                this._folding.delete(container);
                container.destroy({ children: true });
                return;
            }
            requestAnimationFrame(fold);
        };
        requestAnimationFrame(fold);
    }

    /** a player drawn on foot again: in their floor's container, their own size */
    private _ground(player: Player): void {
        const container = player.container;
        container.scale.set(player.sizeMod);
        container.tint = 0xffffff;
        container.visible = !player.dead;
        player.restoreAfterAir();
    }

    /**
     * a player put out up in the air (their 30 s ran out in a plane or under a chute): no landing (no dust, no step, no
     * chute folding: 1.06.1's audit), their chute gone, and drawn as on the ground again, where their body lies
     */
    putOut(player: Player): void {
        this.forget(player);
        this._ground(player);
        if (player.isActivePlayer) {
            this._ownGround = this._ownLast = undefined;
            this._ownVelocity = Vec(0, 0);
            this._drift.reset();
            this._landing.reset();
            this._serverSpot = undefined;
        }
    }

    /** a player gone from the screen (out of view, or their object gone): their chute with them, at once */
    forget(player: Player): void {
        this._untoss(player);
        const view = this._chutes.get(player);
        if (!view) return;
        this._chutes.delete(player);
        view.container.destroy({ children: true });
        view.shadow.destroy();
        view.bodyShadow.destroy();
    }

    /** every frame, before the camera: the planes, the camera of the player aboard, the minimap and the HUD */
    update(): void {
        const data = this._data;
        const me = Game.activePlayer;
        this._sendKeptJump();
        if (data && !data.over) this._updatePlanes(data);
        // everyone in the air, placed (after their containers' own placing this frame)
        this._smoothing = GameConsole.getBuiltInCVar("cv_movement_smoothing");
        for (const player of Game.objects.getCategory(ObjectCategory.Player)) {
            if (player.air && !player.destroyed) this.placeAirborne(player);
        }
        // (on a short screen, a phone's, the HUD's top takes a third of it: as you come down, the ground point a fifth of
        // the screen under the middle, so you, your chute and YOU aren't under the banner and the count: 1.06's lab)
        const now = performance.now();
        const falling = me?.air !== undefined && !me.air.aboard && this._ownGround !== undefined;
        const lift = CameraManager.height < JumpIn.shortScreen && falling && this._playing() ? CameraManager.height * SHORT_LIFT : 0;
        const dt = this._liftAt === undefined ? 0 : Math.min(1000, now - this._liftAt);
        this._liftAt = now;
        this._lift += (lift - this._lift) * (1 - Math.exp(-dt / 180));
        if (Math.abs(this._lift - lift) < 0.5) this._lift = lift;
        const up = this._lift * this._pxPerScreenPx();
        // the camera: aboard, with the plane; under a chute, over where you are on the ground (you're drawn up from it)
        if (me?.air?.aboard && data?.plane !== undefined) {
            const at = this.planePosition(data.plane);
            if (at) CameraManager.position = toPixiCoords(at);
        } else if (me?.air && this._ownGround) {
            CameraManager.position = Vec(this._ownGround.x, this._ownGround.y - up);
        } else if (me && this._lift > 0) {
            // (just down: the lift eased off from where the camera is, over the player as the game has it)
            const base = this._smoothing ? me.container.position : toPixiCoords(me.position);
            CameraManager.position = Vec(base.x, base.y - up);
        }
        this._updateMinimap(data);
        this._updateHud(data, me);
    }

    private _updatePlanes(data: JumpInData): void {
        const planes = this._planes ??= [this._makePlane(), this._makePlane()];
        for (const [index, view] of planes.entries()) {
            const route = data.routes[index];
            const time = this._planeTime(index) ?? 0;
            // (from its clock's start: the second plane's players looked at nothing for 2.5 s, their camera on a plane
            // not drawn yet: 1.06.1's audit)
            const shown = time >= -(JumpIn.boardMs + index * JumpIn.staggerMs) && time <= routeMs(route) + JumpIn.exitMs;
            view.sprite.visible = view.shadow.visible = shown;
            if (!shown) {
                view.sound?.stop();
                view.sound = undefined;
                continue;
            }
            const at = planeAt(route, time);
            const heading = routeHeading(route);
            const pixels = toPixiCoords(at);
            view.sprite.position.set(pixels.x, pixels.y);
            view.sprite.rotation = heading;
            // your own see-through: the ground under it is what you're choosing
            view.sprite.alpha = data.plane === index && this._playing() ? OWN_PLANE_ALPHA : 1;
            // at night, as the moonlight has it (it's over the night's dark, as the chutes are); in the sky's light
            view.sprite.tint = WarSky.airTint(0x9aa4c4);
            // its shadow, far below it on the ground (the sun's way: long at dusk, 0.98.6's lighting)
            const away = Lighting.sun.angle;
            view.shadow.position.set(pixels.x + Math.cos(away) * PLANE_SHADOW * PIXI_SCALE, pixels.y + Math.sin(away) * PLANE_SHADOW * PIXI_SCALE);
            view.shadow.rotation = heading;
            // (none under the end screen: the sounds aren't kept up there, and the engine droned on at one level till the
            // plane was gone, 1.06.1's audit; a death's replay is no end screen, its planes heard: 1.06.1's second review)
            if (Game.gameOver && !Game.spectating && !Game.replaying) {
                view.sound?.stop();
                view.sound = undefined;
                continue;
            }
            view.sound ??= SoundManager.play("airdrop_plane", { position: at, falloff: 0.5, maxRange: 256, dynamic: true, loop: true });
            view.sound.position = at;
        }
    }

    private _makePlane(): PlaneView {
        // the transport (tools/jumpin/jump_plane.py), and its own picture in black as its shadow far below it
        const sprite = new TrenchioSprite("jump_plane").setZIndex(PLANE_ZINDEX).setScale(PLANE_SCALE);
        const shadow = new TrenchioSprite("jump_plane").setZIndex(AIR_GROUND_ZINDEX + 0.05).setScale(PLANE_SCALE * 0.92).setTint(0x000000).setAlpha(PLANE_SHADOW_ALPHA);
        sprite.visible = shadow.visible = false;
        this._skyLayer().addChild(sprite);
        CameraManager.addObject(shadow);
        return { sprite, shadow };
    }

    private _dropPlanes(): void {
        for (const view of this._planes ?? []) {
            view.sound?.stop();
            view.sprite.destroy();
            view.shadow.destroy();
        }
        this._planes = undefined;
        MapManager.jumpRoutes.clear();
    }

    /**
     * the part of the screen the ring's arrow keeps to (screen pixels): the screen, less what the HUD covers round its
     * edges (`FRAME_AVOIDS`), each box cut away from the side it's on (in a corner, the side that loses the least), the
     * middle always in it. Read from the page's own boxes, so it follows the HUD's scale (a 1440p screen's is a third
     * bigger), a phone's layout held either way and a language's longer words; a few times a second (1.06's audit: a
     * fixed frame went under the height bar and the count at 1440p, under the minimap's foot, and on a phone held
     * upright didn't hold the screen's middle at all)
     */
    private _ringFrame(): { readonly left: number, readonly top: number, readonly right: number, readonly bottom: number } {
        const width = CameraManager.width, height = CameraManager.height;
        const now = performance.now();
        const cached = this._frame;
        if (cached && now < cached.until && cached.width === width && cached.height === height) return cached;
        const frame = { left: 16, top: 16, right: width - 16, bottom: height - 16 };
        const middleX = width / 2, middleY = height / 2;
        const pad = 14;
        const canvas = Game.pixi.canvas.getBoundingClientRect();
        for (const id of FRAME_AVOIDS) {
            const box = document.getElementById(id)?.getBoundingClientRect();
            if (!box || box.width < 1 || box.height < 1) continue;
            const left = box.left - canvas.left, top = box.top - canvas.top, right = box.right - canvas.left, bottom = box.bottom - canvas.top;
            // (a layer over the whole screen is nobody's edge; one out of the frame already, nothing to cut)
            if (right - left > width * 0.6 && bottom - top > height * 0.6) continue;
            if (right <= frame.left || left >= frame.right || bottom <= frame.top || top >= frame.bottom) continue;
            const across = frame.right - frame.left, down = frame.bottom - frame.top;
            let loss = Infinity;
            let cut: (() => void) | undefined;
            const consider = (amount: number, length: number, make: () => void): void => {
                if (amount > 0 && amount * length < loss) {
                    loss = amount * length;
                    cut = make;
                }
            };
            if (bottom < middleY) consider(bottom + pad - frame.top, across, () => { frame.top = bottom + pad; });
            if (top > middleY) consider(frame.bottom - (top - pad), across, () => { frame.bottom = top - pad; });
            if (right < middleX) consider(right + pad - frame.left, down, () => { frame.left = right + pad; });
            if (left > middleX) consider(frame.right - (left - pad), down, () => { frame.right = left - pad; });
            cut?.();
        }
        // (the middle always in it: on a small screen the arrow may sit by a box rather than the frame shut)
        frame.left = Math.min(frame.left, middleX - 60);
        frame.right = Math.max(frame.right, middleX + 60);
        frame.top = Math.min(frame.top, middleY - 40);
        frame.bottom = Math.max(frame.bottom, middleY + 40);
        const read = { ...frame, width, height, until: now + 400 };
        this._frame = read;
        return read;
    }

    /**
     * the map's roofs and walls on the ground floor, for the ring: every building of the map's (`MapManager.objects`,
     * off the screen too), as the server's `JumpInManager.isOpen` takes them (jumpInLogic.ts `mapRoofs`); made once a map
     */
    private _mapRoofs(): readonly RoofView[] {
        const objects = MapManager.objects;
        if (this._roofs?.from === objects) return this._roofs.list;
        const list = mapRoofs(objects);
        this._roofs = { from: objects, list };
        return list;
    }

    /**
     * the solids this page has been sent that a chute can't come down on, as the server's (the server knows the rest);
     * gathered once an update (0.98.8: every frame of the way down, from every obstacle; they change with the updates)
     */
    private _solidsOf?: { readonly update: number, readonly list: Obstacle[] };
    private _solids(): Obstacle[] {
        if (this._solidsOf?.update !== Game.updates) this._solidsOf = { update: Game.updates, list: [...Game.objects.getCategory(ObjectCategory.Obstacle)].filter(landingSolid) };
        return this._solidsOf.list;
    }

    /**
     * the roofs one comes down on or through (1.08): the map's decks and glass (`mapRoofTops`, made once a map) and the
     * solids on the decks this page has been sent (`deckSolid`)
     */
    private _deckSolidsOf?: { readonly update: number, readonly list: Obstacle[] };
    private _roofTops(): RoofTops {
        const objects = MapManager.objects;
        if (this._tops?.from !== objects) this._tops = { from: objects, tops: mapRoofTops(objects) };
        const { decks, glass } = this._tops.tops;
        // (once an update, as `_solids`)
        if (this._deckSolidsOf?.update !== Game.updates) this._deckSolidsOf = { update: Game.updates, list: decks.length > 0 ? [...Game.objects.getCategory(ObjectCategory.Obstacle)].filter(deckSolid) : [] };
        return { decks, glass, solids: this._deckSolidsOf.list };
    }

    /**
     * the ring where you'll come down, steered as you're steering now; aimed at a roof or the water, amber, on the open
     * ground nearest (a cross where you're aimed, dots to it): the chute glides there in its last seconds
     * (`JumpInManager.settleSpot`, 1.06.1). Placed by the camera as it's drawn this frame (`afterCamera`)
     */
    private _updateRing(me: Player | undefined): void {
        // (nor under the end screen: won with your chute still up)
        // (nor spun in a tornado, 0.98.4: it sets you down where its path ends, nothing to steer)
        const falling = me?.air !== undefined && !me.air.aboard && !me.air.tossed && this._playing() && !me.dead && !Game.gameOver;
        if (!falling || !me?.air) {
            if (this._ring) this._ring.visible = false;
            if (this._ringLabel) this._ringLabel.visible = false;
            if (this._ringArrow) this._ringArrow.visible = false;
            return;
        }
        const movement = InputManager.movement;
        const direction = steerDirection({
            ...movement,
            moving: InputManager.isMobile && movement.moving,
            angle: InputManager.movementAngle
        });
        // where you're aimed (where you come down steered as you're steering now) and, aimed at a roof, the water or a
        // solid, the open ground nearest that the chute glides to in its last seconds and comes down on (1.06.1: in 1.06
        // it came down on the roof and was moved there as it landed): every roof of the map's, from the map; the water
        // from the map; the solids this page has been sent (jumpInLogic.ts `LandingRing`)
        // (braking, 1.09: the way down takes longer, as long as the key's held; the ring says where that lands you)
        const left = fallLeft(me.air.height, InputManager.jumpHeld);
        const found = this._landing.find(
            performance.now(), me.position, this._ownVelocity, direction, left, me.hitbox.radius,
            MapManager, this._mapRoofs(), this._solids(), this._roofTops()
        );
        const { aim, ground } = found;
        // (in the final approach, where the server takes it: the page's own reckoning beforehand)
        const spot = this._serverSpot && found.settling && left <= JumpIn.settleMs ? this._serverSpot : found.spot;
        const blocked = ground === "roof" || ground === "water" ? ground : undefined;

        const ring = this._ring ??= (() => {
            // (in the sky's container, under everyone up there: shown over the gas's cloud where you'd land in it)
            const graphics = new Graphics();
            graphics.zIndex = AIR_GROUND_ZINDEX + 0.3;
            this._skyLayer().addChild(graphics);
            return graphics;
        })();
        const color = blocked ? 0xe8a33a : 0xffffff;
        const pulse = 0.5 + 0.5 * Math.sin(performance.now() / 160);
        const center = toPixiCoords(spot);
        // (the ring is a place on the ground, a player's width and more; its line, its dot and its words are as big on
        // the screen at every zoom)
        const k = this._pxPerScreenPx();
        const radius = Math.max(4.5 * PIXI_SCALE, 22 * k);
        ring.clear();
        // a dark ring under the dashes, so it shows on snow and sand as on grass
        ring.circle(center.x, center.y, radius).stroke({ width: (RING_LINE + 3) * k, color: 0x000000, alpha: 0.28 });
        for (let i = 0; i < 14; i++) {
            const a0 = (i / 14) * Math.PI * 2 + performance.now() / 2400, a1 = a0 + (Math.PI * 2) / 14 * 0.55;
            ring.moveTo(center.x + Math.cos(a0) * radius, center.y + Math.sin(a0) * radius).arc(center.x, center.y, radius, a0, a1);
        }
        ring.stroke({ width: RING_LINE * k, color, alpha: 0.65 + 0.3 * pulse });
        ring.circle(center.x, center.y, (RING_DOT + 1.5) * k).fill({ color: 0x000000, alpha: 0.3 });
        ring.circle(center.x, center.y, RING_DOT * k).fill({ color, alpha: 0.95 });
        // aimed at a roof or the water: a cross where you're aimed, and dots from it to the ring
        if (blocked && spot !== aim) {
            const from = toPixiCoords(aim);
            const dx = center.x - from.x, dy = center.y - from.y;
            const length = Math.hypot(dx, dy);
            const step = 14 * k;
            if (length > radius + 2 * step) {
                const ux = dx / length, uy = dy / length;
                for (const [dot, fill] of [[3.4, { color: 0x000000, alpha: 0.3 }], [2.2, { color, alpha: 0.8 }]] as const) {
                    for (let along = step; along < length - radius - step * 0.4; along += step) {
                        ring.circle(from.x + ux * along, from.y + uy * along, dot * k);
                    }
                    ring.fill(fill);
                }
            }
            const arm = 7 * k;
            for (const [width, stroke] of [[5.5, { color: 0x000000, alpha: 0.3 }], [2.5, { color, alpha: 0.9 }]] as const) {
                ring.moveTo(from.x - arm, from.y - arm).lineTo(from.x + arm, from.y + arm)
                    .moveTo(from.x + arm, from.y - arm).lineTo(from.x - arm, from.y + arm)
                    .stroke({ ...stroke, width: width * k });
            }
        }
        ring.visible = true;

        // (1.08: a roof's deck one comes down on, a roof's glass one comes down through)
        const words = blocked === "roof"
            ? t("NOT ON A ROOF: YOU'LL LAND BESIDE IT")
            : blocked === "water"
                ? (MapManager.terrain.waterType === FloorNames.Ice ? t("NOT ON THE ICE: YOU'LL LAND ON THE SHORE") : t("NOT IN THE WATER: YOU'LL LAND ON THE SHORE"))
                : ground === "deck"
                    ? t("YOU LAND ON THE ROOF")
                    : ground === "glass" ? t("THROUGH THE GLASS: YOU'LL LAND INSIDE") : t("YOU LAND HERE");
        const label = this._ringLabel ??= (() => {
            const text = new Text({ text: "", style: { fontFamily: "TH-Oswald, Oswald, Arial Narrow, sans-serif", fontSize: LABEL_FONT, fontWeight: "600", fill: 0xffffff, dropShadow: { color: 0x000000, alpha: 0.75, blur: 8, distance: 2 } } });
            text.anchor.set(0, 0.5);
            text.zIndex = AIR_GROUND_ZINDEX + 0.31;
            this._skyLayer().addChild(text);
            return text;
        })();
        if (words !== this._ringWords) {
            this._ringWords = words;
            const language = selectedLanguageCode();
            label.text = canvasLine(words, language);
            label.style.letterSpacing = spacedLetters(language) ? 2 : 0;
        }
        label.style.fill = color;
        label.scale.set((LABEL_PX * k) / LABEL_FONT);

        // on the screen, inside the HUD's frame
        const camera = CameraManager.container;
        const scale = camera.scale.x;
        const screenX = center.x * scale + camera.position.x, screenY = center.y * scale + camera.position.y;
        const box = this._ringFrame();
        // the words beside the ring: to its right (to its left in Arabic, whose words read from the right), unless they'd
        // run out of the frame there and there's more room on the other side
        const gap = radius + 10 * k;
        const wide = label.width * scale;
        const rightRoom = box.right - (screenX + gap * scale), leftRoom = screenX - gap * scale - box.left;
        let side = RIGHT_TO_LEFT.has(selectedLanguageCode()) ? -1 : 1;
        if (side > 0 ? rightRoom < wide && leftRoom > rightRoom : leftRoom < wide && rightRoom > leftRoom) side = -side;
        label.anchor.set(side > 0 ? 0 : 1, 0.5);
        label.position.set(center.x + side * gap, center.y);
        label.visible = true;
        // (and on the screen whatever: moved in from an edge they'd run off, a phone held upright is narrow)
        const keepOnScreen = (): void => {
            const from = label.position.x * scale + camera.position.x - label.anchor.x * wide;
            const shift = Math.max(0, 8 - from) - Math.max(0, from + wide - (CameraManager.width - 8));
            if (shift !== 0) label.position.x += shift / scale;
        };
        keepOnScreen();

        // steered hard, the ring is hundreds of units off: an arrow at the frame's edge points to it, its words by it
        const arrow = this._ringArrow ??= (() => {
            const graphics = new Graphics();
            graphics.zIndex = AIR_GROUND_ZINDEX + 0.32;
            this._skyLayer().addChild(graphics);
            return graphics;
        })();
        if (screenX >= box.left && screenX <= box.right && screenY >= box.top && screenY <= box.bottom) {
            arrow.visible = false;
            return;
        }
        // where the line from the screen's middle to the ring leaves the frame (the middle is always inside it)
        const middleX = CameraManager.width / 2, middleY = CameraManager.height / 2;
        const dx = screenX - middleX, dy = screenY - middleY;
        let reach = 1;
        if (dx > 0) reach = Math.min(reach, (box.right - middleX) / dx);
        if (dx < 0) reach = Math.min(reach, (box.left - middleX) / dx);
        if (dy > 0) reach = Math.min(reach, (box.bottom - middleY) / dy);
        if (dy < 0) reach = Math.min(reach, (box.top - middleY) / dy);
        reach = Math.max(0, reach);
        const edgeX = (middleX + dx * reach - camera.position.x) / scale;
        const edgeY = (middleY + dy * reach - camera.position.y) / scale;
        const angle = Math.atan2(dy, dx);
        const size = 16 * k;
        const point = (forward: number, sideways: number): number[] => [
            edgeX + Math.cos(angle) * forward - Math.sin(angle) * sideways,
            edgeY + Math.sin(angle) * forward + Math.cos(angle) * sideways
        ];
        arrow.clear()
            .poly([...point(size, 0), ...point(-size * 0.6, -size * 0.85), ...point(-size * 0.25, 0), ...point(-size * 0.6, size * 0.85)])
            .fill({ color, alpha: 0.65 + 0.3 * pulse })
            .stroke({ width: 2 * k, color: 0x000000, alpha: 0.4 });
        arrow.visible = true;
        // the words inward of the arrow, on its side of the screen
        const back = 24 * k;
        const wordsX = edgeX - Math.cos(angle) * back, wordsY = edgeY - Math.sin(angle) * back;
        const sideways = Math.abs(dx * (box.bottom - box.top)) > Math.abs(dy * (box.right - box.left));
        if (sideways) label.anchor.set(dx > 0 ? 1 : 0, 0.5);
        else label.anchor.set(0.5, dy > 0 ? 1 : 0);
        label.position.set(wordsX, wordsY);
        keepOnScreen();
    }

    /** the planes' ways on the minimap (and the big map): dashed, the plane on each, yours blue */
    private _updateMinimap(data: JumpInData | undefined): void {
        const graphics = MapManager.jumpRoutes;
        if (!data || data.over) {
            // (cleared once: nothing more to draw after the planes)
            if (!this._minimapClear) graphics.clear();
            this._minimapClear = true;
            return;
        }
        this._minimapClear = false;
        graphics.clear();
        for (const [index, route] of data.routes.entries()) {
            const time = this._planeTime(index) ?? 0;
            if (time > routeMs(route) + JumpIn.exitMs) continue;
            const yours = data.plane === index && this._playing();
            const color = yours ? 0x9db8ff : 0xffffff;
            // the way still ahead of the plane, dashed (none once it's past its way's end, flying off)
            const from = planeAt(route, Math.max(0, time));
            const length = time >= routeMs(route) ? 0 : Vec.len(Vec.sub(route.end, from));
            const step = Vec.normalizeSafe(Vec.sub(route.end, route.start));
            for (let along = 0; along < length; along += 30) {
                const a = Vec.add(from, Vec.scale(step, along)), b = Vec.add(from, Vec.scale(step, Math.min(length, along + 18)));
                graphics.moveTo(a.x, a.y).lineTo(b.x, b.y);
            }
            graphics.stroke({ width: 4, color, alpha: yours ? 0.95 : 0.6 });
            // the plane: an arrowhead its way
            const at = planeAt(route, time);
            const heading = routeHeading(route);
            const point = (forward: number, side: number): number[] => [
                at.x + Math.cos(heading) * forward - Math.sin(heading) * side,
                at.y + Math.sin(heading) * forward + Math.cos(heading) * side
            ];
            graphics.poly([...point(22, 0), ...point(-14, -14), ...point(-6, 0), ...point(-14, 14)]).fill({ color, alpha: 1 });
        }
    }

    private _makeHud(): NonNullable<JumpInViewClass["_hud"]> | undefined {
        const gameUi = document.getElementById("game-ui");
        if (!gameUi) return undefined;
        const root = document.createElement("div");
        root.id = "jump-hud";
        root.hidden = true;
        // (in Arabic each box's words read right to left, the boxes where they are: utils/translations/languageLook.ts)
        root.innerHTML = `<div class="jh-banner"><span class="jh-title rtl-words"></span><span class="jh-lines rtl-words"><span class="jh-line1"></span><span class="jh-line2"></span></span></div><div class="jh-count rtl-words" hidden></div>`;
        const height = document.createElement("div");
        height.id = "jump-height";
        height.hidden = true;
        height.innerHTML = `<span class="jh-height-label rtl-words"></span><div class="jh-bar"><i></i><i></i><i></i><i></i><i></i><i></i><div class="jh-fill"></div><div class="jh-marker"></div></div><span class="jh-height-value rtl-words"></span>`;
        const button = document.createElement("button");
        button.type = "button";
        button.id = "btn-jump";
        button.hidden = true;
        // a press on it jumps (and never reaches the game as a shot)
        for (const type of ["pointerdown", "mousedown", "mouseup", "click", "pointerup"]) button.addEventListener(type, event => event.stopPropagation());
        button.addEventListener("pointerup", () => {
            button.blur();
            this.jump();
        });
        // under a chute the button is BRAKE: held, it brakes (1.09), as the jump key held does
        button.addEventListener("pointerdown", () => { InputManager.jumpHeld = true; });
        for (const type of ["pointerup", "pointercancel", "pointerleave", "lostpointercapture"]) {
            button.addEventListener(type, () => { InputManager.jumpHeld = false; });
        }
        // the banner in the top middle's column, under the scopes: the gas's words (TOXIC GAS ADVANCES IN …) and the
        // spectating line stack under it there rather than on it
        const scopes = document.getElementById("scopes-container");
        if (scopes?.parentElement?.id === "center-top-container") scopes.after(root);
        else gameUi.append(root);
        gameUi.append(height, button);
        const find = (parent: HTMLElement, selector: string): HTMLElement => parent.querySelector<HTMLElement>(selector) ?? parent;
        return this._hud = {
            root,
            title: find(root, ".jh-title"),
            line1: find(root, ".jh-line1"),
            line2: find(root, ".jh-line2"),
            count: find(root, ".jh-count"),
            height,
            heightLabel: find(height, ".jh-height-label"),
            heightFill: find(height, ".jh-fill"),
            heightMarker: find(height, ".jh-marker"),
            heightValue: find(height, ".jh-height-value"),
            button
        };
    }

    /** a key's name as the HUD shows it */
    private _keyName(input: string): string {
        switch (input) {
            case "Space": return t("SPACE");
            case "Mouse0": return t("Left click");
            case "Mouse1": return t("Middle click");
            case "Mouse2": return t("Right click");
            case "MWheelUp": return t("Wheel up");
            case "MWheelDown": return t("Wheel down");
            case "Escape": return t("Esc");
            // (as the controls card shows them)
            case "ArrowUp": return "\u2191";
            case "ArrowDown": return "\u2193";
            case "ArrowLeft": return "\u2190";
            case "ArrowRight": return "\u2192";
            default: return input.length === 1 ? input.toUpperCase() : input;
        }
    }

    /** the steering keys as the HUD shows them: the first key of each way, up, left, down, right (W A S D by default) */
    private _steerKeys(): string[] {
        const first = (action: string): string => InputManager.binds.getInputsBoundToAction(action).find(input => input !== "") ?? "";
        return ["+up", "+left", "+down", "+right"].map(first).filter(key => key !== "").map(key => this._keyName(key));
    }

    /** a line of words with keys in it: the phrase's words round its `<name>`, each key in a box */
    private _withKeys(element: HTMLElement, phrase: string, name: string, keys: readonly string[]): void {
        const at = phrase.indexOf(`<${name}>`);
        const before = at === -1 ? phrase : phrase.slice(0, at);
        const after = at === -1 ? "" : phrase.slice(at + name.length + 2);
        const boxes = keys.map(key => {
            const box = document.createElement("kbd");
            box.textContent = key;
            return box;
        });
        element.replaceChildren(document.createTextNode(before), ...boxes, document.createTextNode(after));
    }

    private _updateHud(data: JumpInData | undefined, me: Player | undefined): void {
        const playing = me !== undefined && !me.dead && this._playing() && Game.gameStarted && !Game.gameOver;
        const air = playing ? me?.air : undefined;
        const plane = data?.plane;
        let state: HudState = "hidden";
        if (air?.aboard && plane !== undefined) state = (this._planeTime(plane) ?? -1) < 0 ? "boarding" : "aboard";
        else if (air?.tossed) state = "tossed";
        else if (air && !air.aboard) state = "falling";

        if (state === "hidden" && !this._hud) return;
        const hud = this._hud ?? this._makeHud();
        if (!hud) return;
        const mobile = InputManager.isMobile;
        const time = plane !== undefined ? this._planeTime(plane) : undefined;
        const left = plane !== undefined && time !== undefined && data ? routeMs(data.routes[plane]) - time : Infinity;
        const seconds = (ms: number): number => Math.max(0, Math.ceil(ms / 1000));
        const jumpKey = InputManager.binds.getInputsBoundToAction("jump").find(input => input !== "");
        const canJump = state === "aboard" && this._canJump();
        const height = air ? Math.round(air.height) : 0;
        const stillAboard = data && !data.over ? data.aboard[0] + data.aboard[1] : 0;
        const steerKeys = state === "falling" ? this._steerKeys() : [];
        const inAir = state === "falling" || state === "tossed";
        // (the brake: the jump key held under the chute, 1.09)
        const braking = state === "falling" && (air?.braking === true || InputManager.jumpHeld);
        // (the big map open: the banner and the height off it, its top is the planes' ways; a phone's JUMP stays)
        const bigMap = MapManager.expanded;
        const key = [
            state,
            bigMap,
            selectedLanguageCode(),
            mobile,
            jumpKey ?? "",
            canJump,
            braking,
            state === "boarding" && time !== undefined ? seconds(-time) : "",
            state === "aboard" && left <= JumpIn.warnMs ? seconds(left) : "",
            plane !== undefined && data ? data.aboard[plane] : "",
            inAir ? `${stillAboard}|${height}|${steerKeys.join(" ")}` : ""
        ].join("~");
        if (key === this._hudKey) return;
        this._hudKey = key;

        hud.root.hidden = state === "hidden" || bigMap;
        hud.height.hidden = !inAir || bigMap;
        // (a phone's button: JUMP in the plane, BRAKE under the chute, held)
        hud.button.hidden = !mobile || (state !== "aboard" && state !== "boarding" && state !== "falling");
        hud.button.disabled = state === "falling" ? false : !canJump;
        hud.button.textContent = state === "falling" ? t("BRAKE") : t("JUMP");
        hud.button.classList.toggle("ji-brake", state === "falling");
        hud.button.classList.toggle("ji-braking", braking);
        if (state === "hidden") return;
        hud.root.dataset.state = state;
        hud.title.textContent = state === "tossed" ? t("CAUGHT IN A TORNADO") : t("JUMP IN");
        switch (state) {
            case "tossed":
                // (0.98.4: spun five seconds, set down where the tornado's path ends; nothing to do but wait)
                hud.line1.textContent = t("It spins you round for five seconds");
                hud.line2.textContent = t("It sets you down where its path ends");
                break;
            case "boarding":
                hud.line1.textContent = t("Your plane reaches the map in <n> s", { n: seconds(-(time ?? 0)) });
                hud.line2.textContent = t("Everyone starts in a plane: jump when you like and steer your chute to where you land");
                break;
            case "aboard":
                if (mobile) hud.line1.textContent = t("Tap JUMP to jump");
                else if (jumpKey === undefined) hud.line1.textContent = t("Set a key for Jump in Settings");
                else this._withKeys(hud.line1, t("Press <key> to jump"), "key", [this._keyName(jumpKey)]);
                hud.line2.textContent = left <= JumpIn.warnMs
                    ? t("The plane leaves the map in <n> s: whoever is still aboard jumps there", { n: seconds(left) })
                    : t("Land in town to fight early, or out on the edge to loot in peace");
                break;
            case "falling":
                if (mobile) {
                    hud.line1.textContent = t("Steer with the stick");
                    hud.line2.textContent = t("Hold BRAKE to come down slower and reach further");
                } else {
                    this._withKeys(hud.line1, t("Steer with <keys>"), "keys", steerKeys);
                    if (jumpKey === undefined) hud.line2.textContent = t("You come down in the ring");
                    else this._withKeys(hud.line2, t("Hold <key> to brake: slower down, further"), "key", [this._keyName(jumpKey)]);
                }
                break;
        }
        hud.root.classList.toggle("ji-warn", state === "aboard" && left <= JumpIn.warnMs);
        const aboard = state === "falling" ? stillAboard : plane !== undefined && data ? data.aboard[plane] : 0;
        hud.count.hidden = !(aboard > 0) || state === "tossed";
        hud.count.textContent = state === "falling" ? t("<n> still in the planes", { n: aboard }) : t("<n> still in your plane", { n: aboard });

        if (inAir) {
            // (a tornado lifts to 9 m, not the chute's 80: the bar is to its own top: 0.98.5's audit)
            const share = Numeric.clamp(height / (state === "tossed" ? Tornadoes.spinHeight : JumpIn.height), 0, 1);
            // (braked: the bar says so, and lights up)
            hud.heightLabel.textContent = braking ? t("BRAKING") : t("HEIGHT");
            hud.height.classList.toggle("ji-braking", braking);
            hud.heightFill.style.height = `${(share * 100).toFixed(1)}%`;
            hud.heightMarker.style.bottom = `${(share * 100).toFixed(1)}%`;
            hud.heightValue.textContent = t("<n> m", { n: height });
        }
    }

    /** a new match, a switch of view, the menu: nothing of the last one left */
    reset(): void {
        this._data = undefined;
        this._zero = undefined;
        this._boardedAt = undefined;
        this._dropPlanes();
        for (const player of [...this._chutes.keys()]) this.forget(player);
        for (const player of [...this._tossed.keys()]) this._untoss(player);
        this._ownGround = this._ownLast = undefined;
        this._ownVelocity = Vec(0, 0);
        this._drift.reset();
        this._ring?.destroy();
        this._ring = undefined;
        this._ringLabel?.destroy();
        this._ringLabel = undefined;
        this._ringArrow?.destroy();
        this._ringArrow = undefined;
        for (const container of this._folding) if (!container.destroyed) container.destroy({ children: true });
        this._folding.clear();
        this._ringWords = "";
        this._hudKey = "";
        this._frame = undefined;
        this._lift = 0;
        this._liftAt = undefined;
        this._minimapClear = false;
        this._landing.reset();
        this._serverSpot = undefined;
        this._jumpKept = undefined;
        this._catchingUp = false;
        if (this._hud) {
            this._hud.root.hidden = this._hud.height.hidden = this._hud.button.hidden = true;
        }
    }
}

export const JumpInView = new JumpInViewClass();

/** the planes' ways (for tests and the debug tools): this match's, as the server sent them */
export const jumpRoutesShown = (): readonly JumpRoute[] | undefined => (JumpInView as unknown as { _data?: JumpInData })._data?.routes;
